import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  styled,
  alpha,
} from '@mui/material'
import * as BABYLON from '@babylonjs/core'
import { getTopicType } from '../renderer/main'

// Modern styled components with Figma-level design
const ModernSettingsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  width: '100%',
  padding: '16px 20px 20px 20px', // Add padding to prevent touching rounded corners
  [theme.breakpoints.down('md')]: {
    padding: '12px 16px 16px 16px', // Responsive padding for mobile
  },
}))

const SettingsSection = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #f1f5f9',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  overflow: 'visible',
  '&:hover': {
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
}))

const SectionContent = styled(CardContent)(({ theme }) => ({
  padding: '20px',
  '&:last-child': {
    paddingBottom: '20px',
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: '4px',
}))

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#64748b',
  marginBottom: '16px',
  fontWeight: 400,
}))

const SliderContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  '&:last-child': {
    marginBottom: '0',
  },
}))

const SliderLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
}))

const SliderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
}))

const SliderValue = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: PURPLE_THEME.primary,
  backgroundColor: alpha(PURPLE_THEME.primary, 0.1),
  padding: '2px 8px',
  borderRadius: '4px',
  minWidth: '32px',
  textAlign: 'center',
}))

// Custom purple theme colors
const PURPLE_THEME = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  primaryLight: '#a78bfa',
  primaryBg: '#f3f4f6',
  primaryBorder: '#e5e7eb',
  text: '#1f2937',
  textSecondary: '#6b7280',
  white: '#ffffff',
}

const ModernSlider = styled(Slider)(({ theme }) => ({
  color: PURPLE_THEME.primary,
  height: '6px',
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: '20px',
    width: '20px',
    backgroundColor: PURPLE_THEME.white,
    border: `2px solid ${PURPLE_THEME.primary}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',

    top: '50%',
    transform: 'translate(-50%, -50%)',

    '&:hover': {
      boxShadow: `0 4px 8px ${alpha(PURPLE_THEME.primary, 0.3)}`,
      transform: 'translate(-50%, -50%) scale(1.1)',
    },
    '&:focus, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: `0 4px 8px ${alpha(PURPLE_THEME.primary, 0.3)}`,
      transform: 'translate(-50%, -50%) scale(1.1)',
    },
  },
  '& .MuiSlider-track': {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: PURPLE_THEME.primary,
    border: 'none',
  },
  '& .MuiSlider-rail': {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: PURPLE_THEME.primaryBorder,
    opacity: 1,
  },
  '& .MuiSlider-mark': {
    backgroundColor: PURPLE_THEME.textSecondary,
    height: '8px',
    width: '2px',
    borderRadius: '1px',
    '&.MuiSlider-markActive': {
      backgroundColor: PURPLE_THEME.primary,
    },
  },
}))

const ModernCheckbox = styled(Checkbox)(({ theme }) => ({
  color: PURPLE_THEME.textSecondary,
  padding: '6px',
  '&.Mui-checked': {
    color: PURPLE_THEME.primary,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}))

const CheckboxLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: '0',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginLeft: '8px',
  },
}))

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  marginTop: '8px',
}))

const StatItem = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid #f1f5f9',
}))

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#64748b',
  fontWeight: 500,
  marginBottom: '2px',
}))

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1e293b',
}))

const ModernDivider = styled(Divider)(({ theme }) => ({
  margin: '0',
  borderColor: '#f1f5f9',
}))

interface Props {
  scale: number
  onUpdateScale: (value: number) => void
  tiling: number
  onUpdateTiling: (value: number) => void
  rotation: number
  onUpdateRotation: (value: number) => void
  printTiling: number
  onUpdatePrintTiling: (value: number) => void
  scene: BABYLON.Scene
}

export default function ModernSettingsTabView({
  scale,
  onUpdateScale,
  tiling,
  onUpdateTiling,
  rotation,
  onUpdateRotation,
  printTiling,
  onUpdatePrintTiling,
  scene,
}: Props) {
  const [snapEnabled, setSnapEnabled] = useState(false)
  const [totalVertexCount, setTotalVertexCount] = useState(0)
  const [totalMeshesCount, setTotalMeshesCount] = useState(0)

  const sanitizedScale = Number.isNaN(scale) ? Number(scale) || 100 : scale
  const sanitizedTiling = Number.isNaN(tiling) ? Number(tiling) || 0 : tiling
  const sanitizedRotation = Number.isNaN(rotation) ? Math.round(Number(rotation) || 0) : rotation

  const topicType = getTopicType()
  const isGallery = topicType === 'gallery'

  useEffect(() => {
    const calculateVertexCount = () => {
      let totalVertices = 0
      let totalMeshes = scene.meshes.length

      scene.meshes.forEach((mesh) => {
        if (mesh.getTotalVertices) {
          totalVertices += mesh.getTotalVertices()
        }
      })

      setTotalVertexCount(totalVertices)
      setTotalMeshesCount(totalMeshes)
    }

    calculateVertexCount()

    const onMeshAdded = scene.onNewMeshAddedObservable.add(() => {
      calculateVertexCount()
    })

    const onMeshRemoved = scene.onMeshRemovedObservable.add(() => {
      calculateVertexCount()
    })

    return () => {
      scene.onNewMeshAddedObservable.remove(onMeshAdded)
      scene.onMeshRemovedObservable.remove(onMeshRemoved)
    }
  }, [scene])

  const handleScaleSliderChange = (event: Event, newValue: number | number[]) => {
    onUpdateScale(newValue as number)
  }

  const handleTileSliderChange = (event: Event, newValue: number | number[]) => {
    onUpdateTiling(newValue as number)
  }

  const handleRotationSliderChange = (event: Event, newValue: number | number[]) => {
    let updatedValue = newValue as number

    if (snapEnabled) {
      updatedValue = Math.round(updatedValue / 15) * 15
    }

    onUpdateRotation(updatedValue)
  }

  const handlePrintTileSliderChange = (event: Event, newValue: number | number[]) => {
    onUpdatePrintTiling(newValue as number)
  }

  const handleSnapToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSnapEnabled(event.target.checked)
    if (!snapEnabled) {
      const updatedValue = Math.round(rotation / 15) * 15
      onUpdateRotation(updatedValue)
    }
  }

  return (
    <ModernSettingsContainer>
      {/* Accent Controls */}
      {!isGallery && (
        <SettingsSection>
          <SectionContent>
            <SectionTitle>Accent Controls</SectionTitle>
            <SectionSubtitle>Adjust the appearance of decorative elements</SectionSubtitle>

            <SliderContainer>
              <SliderLabel>
                <SliderTitle>Scale</SliderTitle>
                <SliderValue>{sanitizedScale}%</SliderValue>
              </SliderLabel>
              <ModernSlider
                value={sanitizedScale}
                onChange={handleScaleSliderChange}
                min={0}
                max={100}
                aria-label='Accent scaling'
              />
            </SliderContainer>

            <SliderContainer>
              <SliderLabel>
                <SliderTitle>Tiling</SliderTitle>
                <SliderValue>{sanitizedTiling}x</SliderValue>
              </SliderLabel>
              <ModernSlider
                value={sanitizedTiling}
                onChange={handleTileSliderChange}
                min={1}
                max={4}
                step={1}
                marks
                aria-label='Accent tiling'
              />
            </SliderContainer>

            <SliderContainer>
              <SliderLabel>
                <SliderTitle>Rotation</SliderTitle>
                <SliderValue>{sanitizedRotation}°</SliderValue>
              </SliderLabel>
              <ModernSlider
                value={sanitizedRotation}
                onChange={handleRotationSliderChange}
                min={0.001}
                max={360}
                step={snapEnabled ? 15 : 1}
                marks={snapEnabled}
                aria-label='Accent rotation'
              />
              <Box mt={2}>
                <CheckboxLabel
                  control={<ModernCheckbox checked={snapEnabled} onChange={handleSnapToggle} />}
                  label='Snap to 15° increments'
                />
              </Box>
            </SliderContainer>
          </SectionContent>
        </SettingsSection>
      )}

      {/* Print Controls */}
      <SettingsSection>
        <SectionContent>
          <SectionTitle>Print Settings</SectionTitle>
          <SectionSubtitle>Configure how images are displayed</SectionSubtitle>

          <SliderContainer>
            <SliderLabel>
              <SliderTitle>Tiling</SliderTitle>
              <SliderValue>{printTiling}x</SliderValue>
            </SliderLabel>
            <ModernSlider
              value={printTiling}
              onChange={handlePrintTileSliderChange}
              min={1}
              max={10}
              step={1}
              marks
              aria-label='Print tiling'
            />
          </SliderContainer>
        </SectionContent>
      </SettingsSection>

      {/* Performance Stats */}
      <SettingsSection>
        <SectionContent>
          <SectionTitle>Performance</SectionTitle>
          <SectionSubtitle>Real-time rendering statistics</SectionSubtitle>

          <StatsContainer>
            <StatItem>
              <StatLabel>Vertices</StatLabel>
              <StatValue>{totalVertexCount.toLocaleString()}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Meshes</StatLabel>
              <StatValue>{totalMeshesCount}</StatValue>
            </StatItem>
          </StatsContainer>
        </SectionContent>
      </SettingsSection>
    </ModernSettingsContainer>
  )
}
